import { baseUrl } from "../../../../hooks/useFetch";
import {
  ApprovalStatus,
  InductionData,
  InductionFeedback,
  InductionStatus,
  TeamDetails,
} from "../Types";

const API_ROOT = `${baseUrl}Induction`;

function getAuthHeaders(): HeadersInit {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  try {
    const raw = sessionStorage.getItem("loginData") || localStorage.getItem("loginData");
    if (raw) {
      const data = JSON.parse(raw);
      if (data?.token) {
        headers["Authorization"] = `Bearer ${data.token}`;
      }
    }
  } catch (e) {
    // ignore
  }
  return headers;
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const controller = new AbortController();

  const timeout = setTimeout(() => {
    controller.abort();
  }, 25000);

  try {
    const response = await fetch(`${API_ROOT}${path}`, {
      ...init,
      headers: {
        ...getAuthHeaders(),
        ...init?.headers,
      },
      signal: controller.signal,
    });

    if (!response.ok) {
      throw new Error(
        `Induction API failed: ${response.status} ${response.statusText}`
      );
    }

    return (await response.json()) as T;
  } catch (error) {
    if (error instanceof Error && error.name === "AbortError") {
      throw new Error(`Induction API timeout: ${path}`);
    }

    throw error;
  } finally {
    clearTimeout(timeout);
  }
}

export const inductionService = {
  async getStatus(): Promise<InductionStatus> {
    try {
      const raw = await request<any>("/status");
      if (!raw) return { required: false };
      return {
        required: raw.required ?? raw.Required ?? false,
        isCompleted: raw.isCompleted ?? raw.IsCompleted ?? false,
        isSkipped: raw.isSkipped ?? raw.IsSkipped ?? false,
        lastSectionIndex: raw.lastSectionIndex ?? raw.LastSectionIndex ?? 0,
        bothApproved: raw.bothApproved ?? raw.BothApproved ?? false,
        pendingApproval: raw.pendingApproval ?? raw.PendingApproval ?? false,
      };
    } catch (err) {
      return { required: false };
    }
  },

  async getInduction(): Promise<InductionData> {
    const rawData = await request<any>("/me");
    if (!rawData) {
      throw new Error("Unable to retrieve induction configuration from backend");
    }

    // Normalize both camelCase and PascalCase properties from ASP.NET Core
    const data: InductionData = {
      employeeName: rawData.employeeName || rawData.EmployeeName || "Team Member",
      companyName: rawData.companyName || rawData.CompanyName || "Tek Inspirations LLC",
      branchName: rawData.branchName || rawData.BranchName || "Main Branch",
      businessName: rawData.businessName || rawData.BusinessName,
      departmentName: rawData.departmentName || rawData.DepartmentName,
      companyId: rawData.companyId ?? rawData.CompanyId,
      branchId: rawData.branchId ?? rawData.BranchId,
      departmentId: rawData.departmentId ?? rawData.DepartmentId,
      designationName: rawData.designationName || rawData.DesignationName,
      joiningDate: rawData.joiningDate || rawData.JoiningDate,
      reportingManager: rawData.reportingManager || rawData.ReportingManager,
      buddyName: rawData.buddyName || rawData.BuddyName,
      employeeCode: rawData.employeeCode || rawData.EmployeeCode,
      branchHeadImage: rawData.branchHeadImage || rawData.BranchHeadImage,
      lastSectionIndex: rawData.lastSectionIndex ?? rawData.LastSectionIndex ?? 0,
      sections: (rawData.sections || rawData.Sections || []).map((sec: any) => ({
        key: (sec.key || sec.Key || "").toLowerCase(),
        label: sec.label || sec.Label || "",
        kicker: sec.kicker || sec.Kicker || "",
        title: sec.title || sec.Title || "",
        intro: sec.intro || sec.Intro || "",
        narration: sec.narration || sec.Narration || "",
        videoUrl: sec.videoUrl || sec.VideoUrl,
        hasVideo: sec.hasVideo ?? sec.HasVideo ?? false,
        videoDurationSeconds: sec.videoDurationSeconds ?? sec.VideoDurationSeconds,
        videoDurationFormatted: sec.videoDurationFormatted || sec.VideoDurationFormatted,
        stats: (sec.stats || sec.Stats || []).map((st: any) => ({
          value: String(st.value ?? st.Value ?? ""),
          label: String(st.label ?? st.Label ?? ""),
        })),
        items: (sec.items || sec.Items || []).map((it: any) => ({
          label: it.label || it.Label || "",
          text: it.text || it.Text || "",
          teamLeadId: it.teamLeadId ?? it.TeamLeadId,
          teamLeadName: it.teamLeadName || it.TeamLeadName,
          teamLead: it.teamLead || it.TeamLead,
          teamLeadImage: it.teamLeadImage || it.TeamLeadImage,
          imageUrl: it.imageUrl || it.ImageUrl,
          isClickable: it.isClickable ?? it.IsClickable ?? false,
        })),
      })),
      assignedAsset: rawData.assignedAsset || rawData.AssignedAsset
        ? {
            hasAsset: rawData.assignedAsset?.hasAsset ?? rawData.AssignedAsset?.HasAsset ?? false,
            assetCode: rawData.assignedAsset?.assetCode || rawData.AssignedAsset?.AssetCode,
            assetName: rawData.assignedAsset?.assetName || rawData.AssignedAsset?.AssetName,
            serialNo: rawData.assignedAsset?.serialNo || rawData.AssignedAsset?.SerialNo,
            issueDate: rawData.assignedAsset?.issueDate || rawData.AssignedAsset?.IssueDate,
          }
        : null,
    };

    if (data.sections && data.sections.length > 0) {
      return data;
    }
    throw new Error("No induction sections configured for this employee.");
  },

  async getTeamMembers(
    teamLeadId?: number,
    teamLeadName?: string,
    departmentId?: number
  ): Promise<TeamDetails> {
    try {
      const params = new URLSearchParams();
      if (teamLeadId) params.append("teamLeadId", teamLeadId.toString());
      if (teamLeadName) params.append("teamLeadName", teamLeadName);
      if (departmentId) params.append("departmentId", departmentId.toString());
      const raw = await request<any>(`/team-members?${params.toString()}`);
      if (!raw) throw new Error("No team members data");
      return {
        teamName: raw.teamName || raw.TeamName || "Team Members",
        teamLeadId: raw.teamLeadId ?? raw.TeamLeadId,
        teamLeadName: raw.teamLeadName || raw.TeamLeadName || "",
        memberCount: raw.memberCount ?? raw.MemberCount ?? 0,
        members: (raw.members || raw.Members || []).map((m: any) => ({
          employeeId: m.employeeId ?? m.EmployeeId ?? 0,
          name: m.name || m.Name || "",
          designation: m.designation || m.Designation || "",
          experience: m.experience || m.Experience || "",
          reportingManager: m.reportingManager || m.ReportingManager || "",
          profileImage: m.profileImage || m.ProfileImage,
          isTeamLead: m.isTeamLead ?? m.IsTeamLead ?? false,
        })),
      };
    } catch (err) {
      throw new Error("Unable to retrieve team members from backend");
    }
  },

  async saveProgress(sectionIndex: number): Promise<void> {
    try {
      await request<void>("/progress", {
        method: "POST",
        body: JSON.stringify({ sectionIndex }),
      });
    } catch (err) {
      // Graceful ignore
    }
  },

  async submitFeedback(feedback: InductionFeedback): Promise<void> {
    try {
      await request<void>("/feedback", {
        method: "POST",
        body: JSON.stringify(feedback),
      });
    } catch (err) {
      throw err;
    }
  },

  async complete(): Promise<void> {
    try {
      await request<void>("/complete", { method: "POST" });
    } catch (err) {
      throw err;
    }
  },

  async getApprovalStatus(): Promise<ApprovalStatus> {
    try {
      const raw = await request<any>("/approval-status");
      if (!raw) throw new Error("Unable to retrieve approval status");
      return {
        employeeId: raw.employeeId ?? raw.EmployeeId,
        hrStatus: raw.hrStatus || raw.HrStatus || "Pending",
        branchHeadStatus: raw.branchHeadStatus || raw.BranchHeadStatus || "Pending",
        hrApprovedAt: raw.hrApprovedAt || raw.HrApprovedAt,
        branchHeadApprovedAt: raw.branchHeadApprovedAt || raw.BranchHeadApprovedAt,
        crmAccessGranted: raw.crmAccessGranted ?? raw.CrmAccessGranted ?? false,
        crmAccessGrantedAt: raw.crmAccessGrantedAt || raw.CrmAccessGrantedAt,
        crmName: raw.crmName || raw.CrmName,
        crmUserId: raw.crmUserId || raw.CrmUserId,
        crmUrl: raw.crmUrl || raw.CrmUrl,
        bothApproved: raw.bothApproved ?? raw.BothApproved ?? false,
      };
    } catch (err) {
      throw new Error("Unable to retrieve approval status from backend");
    }
  },
};
